import React, { useState, useEffect } from 'react';
import { Settings, ChevronRight, Info } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { getMatchConfigurationFields } from './scoringConfig';

/**
 * MatchSettingsComponent
 *
 * A reusable component for rendering and managing match/game settings.
 * Uses the configuration from scoringConfig.js MATCH_CONFIG_FIELDS.
 *
 * @param {string}   sportType                 - The sport type (e.g., 'ping pong', 'tennis')
 * @param {Object}   initialSettings           - Initial settings (from tournament or previous game)
 * @param {Function} onSettingsChange          - Called on every field change with updated settings object
 * @param {Function} onNext                    - Called when Continue button is clicked (cleaned settings)
 * @param {boolean}  readOnly                  - All fields read-only
 * @param {boolean}  disabled                  - Alias for readOnly (for use inside disabled forms)
 * @param {boolean}  showNextButton            - Show the Continue button (default: true)
 * @param {boolean}  compact                   - Omit card wrapper, header, and footer — fields only
 * @param {string}   participantsTypeValue     - Controlled value for the participantsType field
 * @param {Function} onParticipantsTypeChange  - Called when participantsType changes
 */
const MatchSettingsComponent = ({
  sportType,
  initialSettings = {},
  onSettingsChange,
  onNext,
  readOnly = false,
  disabled = false,
  showNextButton = true,
  compact = false,
  participantsTypeValue,
  onParticipantsTypeChange
}) => {
  const { theme } = useTheme();
  const isReadOnly = readOnly || disabled;

  // Early return if no sportType provided
  if (!sportType) {
    if (compact) return null;
    return (
      <div className={`${theme.card} rounded-lg border ${theme.border} p-6`}>
        <p className={`text-sm ${theme.textMuted} text-center`}>
          Loading match settings...
        </p>
      </div>
    );
  }

  // Get field configuration for this sport
  const config = getMatchConfigurationFields(sportType);
  // Keep the full field list here (including "swappable") — it drives
  // internal settings state and handleNext's cleaned-settings output, both
  // of which must still carry through whatever value initialSettings
  // provided even when a field isn't rendered. Only renderField() hides
  // "swappable" from display outside compact mode; filtering it out of
  // `fields` itself previously caused handleNext to silently drop it when
  // starting a new tournament game (non-compact mode), even though the
  // per-match override had correctly set it to true.
  const fields = (config?.fields || []).filter(field => field != null);

  // Initialize settings with defaults from config or initial values
  // participantsType is managed externally, so exclude it from internal state
  const [settings, setSettings] = useState(() => {
    const defaultSettings = {};
    fields.forEach(field => {
      if (field.name === 'participantsType') return;
      defaultSettings[field.name] = initialSettings?.[field.name] !== undefined
        ? initialSettings[field.name]
        : field.default;
    });
    return defaultSettings;
  });

  // Notify parent of participantsType on mount so formData is always in sync with the visual default.
  // Covers both single-option sports (auto-forced value) and multi-option sports (default value).
  useEffect(() => {
    const ptField = fields.find(f => f.name === 'participantsType');
    if (!ptField || !onParticipantsTypeChange) return;
    if (ptField.options?.length === 1) {
      // Single-option sports (e.g. cricket = team-only): always force the value,
      // overriding whatever default participantsType was set in the parent.
      onParticipantsTypeChange(ptField.options[0].value);
    } else if (!participantsTypeValue) {
      const defaultValue = ptField.default;
      if (defaultValue) onParticipantsTypeChange(defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push current settings to parent whenever the component becomes editable.
  // Fires on mount if already editable (new tournament flow) and whenever
  // the form switches from view → edit mode — ensuring the parent always has
  // the full settings object even if the user never touches a field.
  useEffect(() => {
    if (!onSettingsChange || isReadOnly) return;
    onSettingsChange(settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnly]);

  // Merge external initialSettings changes into internal state.
  // Returns prev when nothing actually changed — React bails on re-render,
  // preventing the onSettingsChange → initialSettings prop → useEffect loop.
  useEffect(() => {
    if (!initialSettings || Object.keys(initialSettings).length === 0) return;
    setSettings(prev => {
      let changed = false;
      const next = { ...prev };
      Object.keys(initialSettings).forEach(key => {
        if (key === 'participantsType') return; // managed externally
        if (next[key] !== initialSettings[key]) {
          next[key] = initialSettings[key];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [initialSettings]);

  // Handle field value change
  const handleFieldChange = (fieldName, value) => {
    if (isReadOnly) return;
    setSettings(prev => {
      const newSettings = { ...prev, [fieldName]: value };
      if (onSettingsChange) onSettingsChange(newSettings);
      return newSettings;
    });
  };

  // Handle next/continue button
  const handleNext = () => {
    if (onNext) {
      const cleanedSettings = {};
      fields.forEach(field => {
        if (field.name === 'participantsType') return;
        const value = settings[field.name];
        cleanedSettings[field.name] = (value === '' || value === undefined)
          ? field.default
          : value;
      });
      // Always include participantsType so it persists in gameSettings
      if (participantsTypeValue !== undefined) {
        cleanedSettings.participantsType = participantsTypeValue;
      }
      onNext(cleanedSettings);
    }
  };

  // Render a single field
  const renderField = (field) => {
    // "swappable" only makes sense as a per-match decision (e.g. an MLP
    // Dreambreaker needs it on even though the tournament's other matches
    // don't) — so it's hidden from the main tournament-level settings form
    // and the scoring flow's own settings step, and only surfaced in the
    // per-match override modal (compact mode). Its value still flows through
    // normally either way — see the `fields` comment above for why it isn't
    // filtered out of the array itself.
    if (field.name === 'swappable' && !compact) return null;

    // "swapOption" is driven entirely by schedule generation (e.g. MLP's
    // Dreambreaker), never a manual per-match toggle — always hidden from
    // rendering, even in compact mode. Kept in `fields` (see comment above)
    // purely so its value survives the settings round-trip.
    if (field.name === 'swapOption') return null;

    // --- participantsType: special handling ---
    if (field.name === 'participantsType') {
      // Single-option: hidden, auto-applied via the mount effect above
      if (field.options?.length === 1) return null;

      // Multiple options: radio buttons (controlled externally)
      const ptValue = participantsTypeValue || field.default || '';
      return (
        <div key={field.name} className={compact ? 'md:col-span-2' : 'mb-4'}>
          <label className={`block text-sm font-medium ${theme.text} mb-2`}>
            Participants Type {field.required && <span className="text-red-500">*</span>}
          </label>
          <div className="grid sm:grid-cols-2 gap-2">
            {field.options.map(option => (
              <label key={option.value} className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                ptValue === option.value
                  ? 'border-blue-500 bg-blue-500/10'
                  : `${theme.border} ${isReadOnly ? '' : theme.cardHover}`
              } ${isReadOnly ? 'cursor-not-allowed' : ''}`}>
                <input
                  type="radio"
                  name="participantsType"
                  value={option.value}
                  checked={ptValue === option.value}
                  onChange={() => !isReadOnly && onParticipantsTypeChange && onParticipantsTypeChange(option.value)}
                  className="hidden"
                  disabled={isReadOnly}
                />
                <span className={`mr-3 w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  ptValue === option.value ? 'border-blue-500 bg-blue-500' : theme.border
                }`}>
                  {ptValue === option.value && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                </span>
                <span className={`text-sm font-medium ${theme.text}`}>{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    // --- Single-option selects: hide, value is auto-applied via defaults ---
    if (field.type === 'select' && field.options?.length === 1) return null;

    const value = settings[field.name];
    const wrapClass = compact ? '' : 'mb-4';

    switch (field.type) {
      case 'number':
        return (
          <div key={field.name} className={wrapClass}>
            <label className={`block text-sm font-medium ${theme.text} mb-2`}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.description && (
              <p className={`text-xs ${theme.textMuted} mb-2 flex items-start gap-1`}>
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>{field.description}</span>
              </p>
            )}
            <input
              type="number"
              value={value ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                handleFieldChange(field.name, val === '' ? '' : parseInt(val, 10));
              }}
              min={field.min}
              max={field.max}
              step={field.step || 1}
              placeholder={field.placeholder}
              disabled={isReadOnly}
              className={`w-full px-3 py-2 ${theme.input} rounded-lg border ${theme.border}
                focus:ring-2 focus:ring-blue-500 focus:border-transparent
                ${isReadOnly ? 'opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-700' : ''}
                transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
            />
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.name} className={wrapClass}>
            <label className={`flex items-start gap-3 cursor-pointer ${isReadOnly ? 'cursor-not-allowed opacity-60' : ''}`}>
              <input
                type="checkbox"
                checked={value || false}
                onChange={(e) => handleFieldChange(field.name, e.target.checked)}
                disabled={isReadOnly}
                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded
                  focus:ring-blue-500 focus:ring-2
                  disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <div className="flex-1">
                <span className={`block text-sm font-medium ${theme.text}`}>
                  {field.label}
                  {field.required && <span className="text-red-500 ml-1">*</span>}
                </span>
                {field.description && (
                  <span className={`block text-xs ${theme.textMuted} mt-1`}>
                    {field.description}
                  </span>
                )}
              </div>
            </label>
          </div>
        );

      case 'select': {
        const selectedOption = field.options?.find(o => String(o.value) === String(value));
        return (
          <div key={field.name} className={wrapClass}>
            <label className={`block text-sm font-medium ${theme.text} mb-2`}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.description && (
              <p className={`text-xs ${theme.textMuted} mb-2 flex items-start gap-1`}>
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>{field.description}</span>
              </p>
            )}
            <select
              value={value ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                const opt = field.options?.find(o => String(o.value) === raw);
                handleFieldChange(field.name, opt && typeof opt.value === 'number' ? opt.value : raw);
              }}
              disabled={isReadOnly}
              className={`w-full px-3 py-2 ${theme.input} rounded-lg border ${theme.border}
                focus:ring-2 focus:ring-blue-500 focus:border-transparent
                ${isReadOnly ? 'opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-700' : ''}
                transition-all`}
            >
              {field.options && field.options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {selectedOption?.hint && (
              <p className={`mt-1.5 text-xs ${theme.textMuted} flex items-start gap-1`}>
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-blue-500" />
                <span>{selectedOption.hint}</span>
              </p>
            )}
          </div>
        );
      }

      case 'text':
      default:
        return (
          <div key={field.name} className={wrapClass}>
            <label className={`block text-sm font-medium ${theme.text} mb-2`}>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.description && (
              <p className={`text-xs ${theme.textMuted} mb-2 flex items-start gap-1`}>
                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <span>{field.description}</span>
              </p>
            )}
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder={field.placeholder}
              disabled={isReadOnly}
              className={`w-full px-3 py-2 ${theme.input} rounded-lg border ${theme.border}
                focus:ring-2 focus:ring-blue-500 focus:border-transparent
                ${isReadOnly ? 'opacity-60 cursor-not-allowed bg-gray-100 dark:bg-gray-700' : ''}
                transition-all`}
            />
          </div>
        );
    }
  };

  // Determine visible fields (exclude hidden single-option selects)
  const visibleFields = fields.filter(field => {
    if (field.name === 'participantsType' && field.options?.length === 1) return false;
    if (field.type === 'select' && field.options?.length === 1) return false;
    return true;
  });

  // Compact mode: just the fields, no card/header/buttons
  if (compact) {
    if (visibleFields.length === 0) return null;
    return (
      <div className="grid md:grid-cols-2 gap-3">
        {fields.map(field => renderField(field))}
      </div>
    );
  }

  // Full mode (used in scoring/game flows)
  if (fields.length === 0) {
    return (
      <div className={`${theme.card} rounded-lg border ${theme.border} p-6`}>
        <p className={`text-sm ${theme.textMuted} text-center`}>
          No configuration fields available for this sport.
        </p>
      </div>
    );
  }

  return (
    <div className={`${theme.card} rounded-lg border ${theme.border} p-6`}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className={`p-2 rounded-lg ${theme.primary} bg-opacity-10`}>
          <Settings className="w-5 h-5 text-blue-600" />
        </div>
        <div>
          <h3 className={`text-lg font-semibold ${theme.text}`}>
            Match Settings
          </h3>
          <p className={`text-sm ${theme.textMuted}`}>
            {isReadOnly ? 'Tournament settings (read-only)' : 'Configure your match parameters'}
          </p>
        </div>
      </div>

      {/* Read-only indicator */}
      {isReadOnly && (
        <div className={`mb-4 p-3 rounded-lg ${theme.backgroundSecondary} border ${theme.border}`}>
          <p className={`text-xs ${theme.textMuted} flex items-center gap-2`}>
            <Info className="w-4 h-4" />
            Match settings are view-only.
          </p>
        </div>
      )}

      {/* Fields */}
      <div className="space-y-2">
        {fields.map(field => renderField(field))}
      </div>

      {/* Next Button — hidden for non-scorers */}
      {showNextButton && !isReadOnly && (
        <div className="mt-6">
          <button
            onClick={handleNext}
            className={`w-full px-6 py-3 ${theme.primary} text-white rounded-lg
              hover:bg-blue-700 transition-colors flex items-center justify-center gap-2
              font-medium shadow-sm`}
          >
            <span>Select Teams</span>
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Current Settings Summary */}
      {!isReadOnly && (
        <div className="mt-4">
          <details className={`text-xs ${theme.textMuted}`}>
            <summary className="cursor-pointer hover:text-blue-600">
              View current settings
            </summary>
            <pre className={`mt-2 p-2 rounded ${theme.backgroundSecondary} overflow-auto`}>
              {JSON.stringify(settings, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
};

export default MatchSettingsComponent;
